import React, { useEffect, useState } from 'react';
import { 
  MessageCircle, 
  Wifi, 
  WifiOff, 
  Download, 
  RefreshCw, 
  Trash2,
  Copy,
  Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MessageList } from '@/components/chat/MessageList';
import { MessageInput, type FileAttachment } from '@/components/chat/MessageInput';
import type { Message, MessageContent, UIResource } from '@/types/chat';
import { nanoid } from 'nanoid';
import { toast } from 'sonner';
import { useGetBackendUrl } from '@/hooks/use-get-backend-url';
import { format } from 'date-fns';
import { createAuthHeaders } from '@/utils/authHeaders';
import { useIdentity } from '@/hooks/useIdentity';
import { useSubscription } from '@/contexts/SubscriptionContext';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ChatTestPanelProps {
  workflowId: string;
  chatTriggerNodeId: string;
  onClose?: () => void;
  isWorkflowActive: boolean;
  workflowDefinition?: any; // Current workflow definition (for testing unsaved changes)
}

export const ChatTestPanel: React.FC<ChatTestPanelProps> = ({
  workflowId,
  chatTriggerNodeId,
  onClose,
  workflowDefinition,
}) => {
  const backendUrl = useGetBackendUrl();
  const { getAccessToken } = useIdentity();
  const { subscriptionId } = useSubscription();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Generate a proper UUID for initial session (server will create the actual session on first message)
  // Using crypto.randomUUID() to ensure valid GUID format that backend can parse
  const [sessionId, setSessionId] = useState<string>(() => crypto.randomUUID());
  const [isLoading, setIsLoading] = useState(false);
  const [sessionIdCopied, setSessionIdCopied] = useState(false);

  // Extract file upload config from the chat trigger node parameters
  const chatTriggerNode = workflowDefinition?.nodes?.find(
    (n: any) => n.id === chatTriggerNodeId || n.data?.type === 'chat-trigger'
  );
  const triggerParams = chatTriggerNode?.data?.parameters ?? {};
  const allowFileUploads = triggerParams.allowFileUploads === true;
  const allowedFileMimeTypes: string | null = triggerParams.allowedFileMimeTypes ?? null;

  const handleSendMessage = async (content: string, files?: FileAttachment[]) => {
    if (isLoading) return;

    // Create optimistic user message
    const userMessage: Message = {
      id: nanoid(),
      sessionId,
      role: 'user',
      content,
      timestamp: new Date(),
      status: 'sending',
      metadata: files && files.length > 0
        ? { files: files.map(f => ({ fileName: f.fileName, mimeType: f.mimeType })) }
        : undefined,
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    setIsTyping(true);
    setError(null);

    try {
      // Get fresh token from identity provider
      const token = await getAccessToken();
      
      // Send message to test endpoint
      const url = `${backendUrl}/api/chat/${workflowId}/test/stream`;
      console.log('🌐 Sending chat request to:', url);
      console.log('📦 Request body:', { message: content, sessionId });
      
      // Build request body with optional file attachments
      const requestBody: Record<string, any> = {
        message: content,
        sessionId,
        workflowDefinition, // Send current workflow definition (for testing unsaved changes)
      };
      if (files && files.length > 0) {
        requestBody.files = files.map(f => ({
          fileName: f.fileName,
          mimeType: f.mimeType,
          data: f.data,
        }));
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: createAuthHeaders(token, subscriptionId), // Use subscription from context
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Chat API error:', errorData);
        throw new Error(errorData.error || 'Failed to send message');
      }

      console.log('✅ Chat API response received, starting to read stream...');

      // Update user message status
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === userMessage.id ? { ...msg, status: 'sent' as const } : msg
        )
      );

      // Read streaming response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let aiResponseContent = '';
      const aiMessageId = nanoid();
      let buffer = ''; // Buffer to accumulate incomplete SSE messages

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          buffer += chunk;
          
          // Split by double newline (SSE message separator)
          const messages = buffer.split('\n\n');
          
          // Keep the last incomplete message in the buffer
          buffer = messages.pop() || '';

          for (const message of messages) {
            const lines = message.split('\n');
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  console.log('📨 Received SSE data:', data);
                
                if (data.type === 'chunk') {
                  // ✅ Check if content is a UIResource object (MCP-UI support)
                  let messageContent: MessageContent;
                  
                  if (typeof data.content === 'object' && data.content?.type === 'resource') {
                    // UIResource object - skip it here, it will be handled by the 'resource' event
                    console.log('📊 Skipping UIResource in chunk event, will be handled by resource event');
                    continue;
                  } else {
                    // String content - concatenate chunks
                    aiResponseContent += data.content;
                    messageContent = aiResponseContent;
                  }
                  
                  // Update or create AI message
                  setMessages((prev) => {
                    const existingIndex = prev.findIndex(m => m.id === aiMessageId);
                    
                    const aiMessage: Message = {
                      id: aiMessageId,
                      sessionId,
                      role: 'assistant',
                      content: messageContent,
                      timestamp: new Date(),
                      status: 'sent',
                    };

                    if (existingIndex >= 0) {
                      const newMessages = [...prev];
                      newMessages[existingIndex] = aiMessage;
                      return newMessages;
                    } else {
                      return [...prev, aiMessage];
                    }
                  });
                } else if (data.type === 'resource') {
                  // Handle UIResource (MCP-UI support)
                  console.log('📊 Received UIResource event:', data);
                  
                  // The backend sends: { type: 'resource', content: { type: 'resource', resource: {...} } }
                  // We need to extract the inner UIResource object
                  let uiResource: UIResource;
                  
                  if ((data as any).content && typeof (data as any).content === 'object') {
                    // Content is the UIResource object
                    uiResource = (data as any).content as UIResource;
                  } else {
                    // Fallback: data itself might be the UIResource (shouldn't happen but be safe)
                    console.warn('📊 Unexpected resource structure, using data directly');
                    uiResource = data as unknown as UIResource;
                  }
                  
                  console.log('📊 Extracted UIResource:', uiResource);
                  console.log('📊 UIResource has resource field:', 'resource' in uiResource);
                  console.log('📊 UIResource metadata:', uiResource.resource?._meta);
                  
                  // Add UIResource as a separate message (after the text response)
                  const uiResourceMessage: Message = {
                    id: nanoid(),
                    sessionId,
                    role: 'assistant',
                    content: uiResource,
                    timestamp: new Date(),
                    status: 'sent',
                  };
                  
                  setMessages((prev) => [...prev, uiResourceMessage]);
                  setIsTyping(false);
                } else if (data.type === 'done') {
                  // Update sessionId from server response to reuse the same ChatSession for subsequent messages
                  if (data.metadata?.sessionId) {
                    console.log('📝 Updating sessionId from server:', data.metadata.sessionId);
                    setSessionId(data.metadata.sessionId);
                  }

                  // ✅ Check if fullResponse is a UIResource object (MCP-UI support)
                  if (typeof data.fullResponse === 'object' && data.fullResponse?.type === 'resource') {
                    // Update final message with UIResource
                    setMessages((prev) => {
                      const existingIndex = prev.findIndex(m => m.id === aiMessageId);
                      if (existingIndex >= 0) {
                        const newMessages = [...prev];
                        newMessages[existingIndex] = {
                          ...newMessages[existingIndex],
                          content: data.fullResponse as UIResource,
                        };
                        return newMessages;
                      }
                      return prev;
                    });
                  }
                  // Final message
                  setIsTyping(false);
                } else if (data.type === 'error') {
                  // Handle error from workflow execution
                  setIsTyping(false);
                  setIsLoading(false);
                  setError(data.error);
                  
                  // Add error message to chat
                  setMessages((prev) => [
                    ...prev,
                    {
                      id: nanoid(),
                      sessionId,
                      role: 'assistant',
                      content: `❌ Error: ${data.error}`,
                      timestamp: new Date(),
                      status: 'error',
                    },
                  ]);
                  
                  toast.error('Workflow Error', {
                    description: data.error,
                  });
                }
                } catch (parseError) {
                  console.error('Error parsing SSE data:', parseError);
                }
              }
            }
          }
        }
      }

      setIsConnected(true);
    } catch (err) {
      // console.error('Failed to send message:', err);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === userMessage.id ? { ...msg, status: 'error' as const } : msg
        )
      );
      setError(err instanceof Error ? err.message : 'Failed to send message');
      setIsConnected(false);
      
      toast.error('Failed to send message', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const handleNewSession = () => {
    // Generate proper UUID for new session (server will create actual session on first message)
    setSessionId(crypto.randomUUID());
    setMessages([]);
    setError(null);
    setIsConnected(true);

    toast.success('New Session', {
      description: 'Started a new test session',
    });
  };

  const handleClearChat = () => {
    setMessages([]);
    toast.success('Chat Cleared', {
      description: 'All messages have been cleared',
    });
  };

  const handleExportHistory = () => {
    if (messages.length === 0) {
      toast.error('No Messages', {
        description: 'There are no messages to export',
      });
      return;
    }

    // Create export data
    const exportData = {
      sessionId,
      workflowId,
      chatTriggerNodeId,
      exportedAt: new Date().toISOString(),
      messageCount: messages.length,
      messages: messages.map((msg) => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp,
        status: msg.status,
      })),
    };

    // Create blob and download
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-history-${sessionId}-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success('Export Complete', {
      description: `Exported ${messages.length} messages`,
    });
  };

  const handleCopySessionId = async () => {
    if (!sessionId) return;

    try {
      await navigator.clipboard.writeText(sessionId);
      setSessionIdCopied(true);
      setTimeout(() => setSessionIdCopied(false), 2000);
      
      toast.success('Copied', {
        description: 'Session ID copied to clipboard',
      });
    } catch (err) {
      toast.error('Error', {
        description: 'Failed to copy session ID',
      });
    }
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <MessageCircle className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Test Chat Workflow</h3>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              {isConnected ? (
                <>
                  <Wifi className="w-3 h-3 text-green-500" />
                  <span>Connected</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-3 h-3 text-destructive" />
                  <span>Disconnected</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleExportHistory}
                  disabled={messages.length === 0}
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Export History</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleNewSession}
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>New Session</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleClearChat}
                  disabled={messages.length === 0}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Clear Chat</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Session ID - Subtle display */}
      {sessionId && (
        <div className="px-4 py-2 border-b bg-muted/10">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Session ID:</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleCopySessionId}
                    className="flex items-center gap-1 text-xs font-mono text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <span className="truncate max-w-[200px]">{sessionId}</span>
                    {sessionIdCopied ? (
                      <Check className="h-3 w-3 text-green-500" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Click to copy</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="bg-destructive/10 text-destructive px-4 py-2 text-sm border-b">
          {error}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-hidden">
        <MessageList
          messages={messages}
          isTyping={isTyping}
          welcomeMessage="Send a test message to your chat workflow. This is a test session and won't affect production data."
        />
      </div>

      {/* Input */}
      <MessageInput
        onSend={handleSendMessage}
        disabled={!isConnected || isLoading}
        placeholder="Type a test message..."
        maxLength={1000}
        isLoading={isLoading}
        allowFileUploads={allowFileUploads}
        allowedFileMimeTypes={allowedFileMimeTypes}
      />
    </div>
  );
};
